'use client';

import React from 'react';

import {
    Divider,
    Icon,
    IconName,
    Justify,
    Popover,
    Text,
    TextLevels,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import { MobilePlanType } from '@app-types/plan';

import { InlinePrice } from '@components/inline-price';

import { formatPrice } from '@helpers/wording';

import { PricesDetails } from '@product-detail/helpers';
import { ClickEvent } from '@trilogy-ds/react/lib/events/OnClickEvent';

type TicketPlanProps = {
    plan: MobilePlanType;
    openPlanModal: ClickEvent;
    prices: PricesDetails;
    isRenewal: boolean;
};

export function TicketPlan({ plan, openPlanModal, prices, isRenewal }: Readonly<TicketPlanProps>) {
    const { planPrices } = prices;

    const getPromoWording = () => {
        if (!plan?.hasRenewalDiscounts || !plan.renewalDiscounts?.length) {
            return null;
        }

        const discountCount = plan.renewalDiscounts.length;

        return (
            <Text level={TextLevels.THREE} marginless>
                Remise{discountCount > 1 ? 's' : ''} client
                <Popover trigger={<Icon name={IconName.INFOS_CIRCLE} />}>
                    <div className='has-text-left'>
                        <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} marginless>
                            {discountCount > 1 ? 'Remises appliquées' : 'Remise appliquée'}&nbsp;:
                        </Text>
                        {plan.renewalDiscounts.map((discount) => (
                            <Text key={discount.offerId}>
                                {discount.commercialLabel}&nbsp;:{' '}
                                {discount.recurringPrice?.priceIncludingTax &&
                                Math.abs(discount.recurringPrice.priceIncludingTax) >= 0
                                    ? formatPrice(discount.recurringPrice.priceIncludingTax)
                                    : null}
                            </Text>
                        ))}
                    </div>
                </Popover>
            </Text>
        );
    };

    return (
        <>
            <Divider />
            <View flexable justify={Justify.SPACE_BETWEEN}>
                <div>
                    {plan.isOwned ? (
                        <Text level={TextLevels.THREE} marginless>
                            Votre forfait actuel
                        </Text>
                    ) : null}
                    <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} marginless level={TextLevels.TWO}>
                        {isRenewal ? plan.name : plan.sexyName}
                    </Text>
                    {planPrices && Boolean(planPrices.promoDuration && planPrices.promoDuration > 0) && (
                        <Text level={TextLevels.THREE} marginless>
                            {`Pendant ${planPrices.promoDuration} mois puis`}&nbsp;
                            <InlinePrice amount={planPrices.forever} period='mois' level={TextLevels.THREE} typo={[]} />
                        </Text>
                    )}
                    {getPromoWording()}
                    <Text level={TextLevels.THREE} className='capitalize-first'>
                        {plan.obligationLabel}
                    </Text>
                    <Text level={TextLevels.TWO}>
                        <button className='link button-clear' onClick={openPlanModal}>
                            Voir le détail du forfait
                        </button>
                    </Text>
                </div>
                {planPrices && planPrices.final >= 0 && (
                    <InlinePrice level={TextLevels.TWO} amount={planPrices.final} period='mois' />
                )}
            </View>
        </>
    );
}
